import type { KitchenSceneBuilder } from '../kitchen-scene-builder.class';
import type { KitchenBlueprintInput } from '../types';
import type { SceneBuildManifest } from '../../../types/scene-build-manifest.type';
import { normalizeSurroundKey, normalizeCountertopKey, getTrimKeyForSurround, shouldRenderApplianceModel } from '../helpers';

export const buildSceneManifestCfn = (
	self: KitchenSceneBuilder,
	{ input }: { input: KitchenBlueprintInput }
): SceneBuildManifest => {
	const islands = self.kitchenDataManagerAccess.getIslands();
	const kitchenMaterial = self.kitchenDataManagerAccess.getKitchenMaterial();
	const noModelSkus: Array<string> = input.controller?.noModelSkus ?? [];

	const appliances: Array<{ id: string; sku: string; islandIndex: number }> = [];
	const countertops: Array<{ islandIndex: number; key: string | null }> = [];

	for (let islandIndex = 0; islandIndex < islands.length; islandIndex++) {
		const island = islands[islandIndex];

		// Mirror createCountertopObjectCfn's skip conditions exactly — a countertop the builder won't render must not be expected
		const counterVertices = island.counter?.renderingDetails?.vertices;
		const willRenderCountertop = Boolean(island.counter && counterVertices && counterVertices.length > 0);
		countertops.push({ islandIndex, key: willRenderCountertop ? normalizeCountertopKey(island.counter?.material?.key) : null });

		if (!island.panels) continue;

		for (const panel of island.panels) {
			if (!panel.applianceGroups) continue;

			for (const applianceGroup of panel.applianceGroups) {
				if (!applianceGroup.items) continue;

				for (const item of applianceGroup.items) {
					if (item.isSpacer) continue;

					// Use same filter as load-appliances variants
					const infoProxy = {
						sku: item.sku ?? '',
						placeholderModelSku: item.placeholderModelSku ?? null
					};
					if (!shouldRenderApplianceModel(infoProxy as any, noModelSkus)) continue;

					appliances.push({
						id: item.id ?? `item-${islandIndex}-${appliances.length}`,
						sku: item.sku ?? '',
						islandIndex
					});
				}
			}
		}
	}

	const surroundKey = kitchenMaterial?.surround?.key
		? normalizeSurroundKey(kitchenMaterial.surround.key)
		: null;

	// MOK surrounds render no visible corner trim (createTrimPanelCfn skips them), so there is no trim key to verify.
	const trimKey = surroundKey && !surroundKey.startsWith('moks/')
		? getTrimKeyForSurround(surroundKey)
		: null;

	return {
		appliances,
		surroundKey,
		trimKey,
		countertops,
		skyKey: (input as any).skyKey ?? null,
		floorKey: input.floorKey ?? null,
		islandCount: islands.length
	};
};
